import nextI18NextConfig from '../next-i18next.config.js';
import { wrapper } from 'redux/store';

import 'static/assets/css/froala-table.css';
import 'static/assets/css/tailwind.css';

import * as Sentry from '@sentry/react';
import { intercomNode } from 'integrations/intercom';
import { initializeSegmentTrackers } from 'integrations/segment';
import Cookies from 'js-cookie';
import RouteChangeIndicator from 'lib/route-change-indicator';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import { useEffect } from 'react';
import { appWithTranslation } from 'next-i18next';
import { useAppSelector } from 'redux/store/hooks';
import type { NextApiRequest } from 'next';
import { useHeartbeat } from 'hooks/use-heartbeat';
import { Provider } from 'react-redux';

const isServer = typeof window === 'undefined';

declare global {
  interface Window {
    analytics: any;
  }
}

type BHAppProps = AppProps & {
  token: string;
  pageProps: any;
};

if (!isServer) {
  require('lib/initialize-client');

  window.addEventListener('load', function () {
    const el = document.getElementById('holderStyle');
    if (el) {
      el.parentNode?.removeChild(el);
    }
  });
}

const BHApp = ({ Component, pageProps, router, token }: BHAppProps) => {
  const user = useAppSelector((state) => state.currentUser.user);
  const site = useAppSelector((state) => state.currentSite.site);
  const isAdminSegmentAnalyticsEnabled = useAppSelector(
    (state) => state.features.features['segment_analytics_admin']?.enabled,
  );

  const isParticipantSegmentAnalyticsEnabled = useAppSelector(
    (state) =>
      state.features.features['segment_analytics_participant']?.enabled,
  );

  useHeartbeat();

  useEffect(() => {
    if (site?.locale) {
      Cookies.set('current_language', site?.locale, {
        expires: 2,
        path: '/',
        secure: true,
        sameSite: 'Strict',
      });
    }

    initializeSegmentTrackers(
      user,
      site,
      isAdminSegmentAnalyticsEnabled,
      isParticipantSegmentAnalyticsEnabled,
    );

    if (token) {
      sessionStorage.setItem('unexpected_thunder', token);
    }
  }, []);

  return (
    <>
      <Component {...pageProps} />
      <RouteChangeIndicator />
      {intercomNode({ router, pageProps })}
    </>
  );
};

const WrappedApp = (props) => {
  const { store, props: appProps } = wrapper.useWrappedStore(props);
  return (
    <Provider store={store}>
      <BHApp {...appProps} />
    </Provider>
  );
};

WrappedApp.getInitialProps = wrapper.getInitialAppProps(
  (store) => async (appContext: AppContext) => {
    const appProps = await App.getInitialProps(appContext);
    const req = appContext.ctx.req as NextApiRequest;

    // @ts-ignore
    appContext.ctx.reduxStore = store;
    const token = req?.cookies?.['unexpected_thunder'] || null;

    return {
      ...appProps,
      token,
    };
  },
);

export default appWithTranslation(
  Sentry.withProfiler(WrappedApp, { name: 'blue-haven' }),
  nextI18NextConfig as any, // TypeScript workaround for UserConfig type
);
